import * as THREE from 'three';

const isLitMaterial = (material: THREE.Material) =>
    material instanceof THREE.MeshStandardMaterial ||
    material instanceof THREE.MeshPhongMaterial ||
    material instanceof THREE.MeshLambertMaterial;

/**
 * Enables shadow casting/receiving on every lit mesh under `root`. Unlit
 * helpers (arrows, labels, outlines using MeshBasicMaterial) are skipped so
 * they don't throw shadows across the room.
 *
 * @param {THREE.Object3D} root - The object whose meshes should be updated.
 * @param {object} options - Which shadow flags to enable.
 * @param {boolean} [options.cast] - Whether meshes cast shadows.
 * @param {boolean} [options.receive] - Whether meshes receive shadows.
 */
export const applyShadows = (
    root: THREE.Object3D,
    {cast = true, receive = true}: {cast?: boolean; receive?: boolean} = {}
) => {
    root.traverse((object) => {
        if (!(object instanceof THREE.Mesh)) return;

        const materials = Array.isArray(object.material)
            ? object.material
            : [object.material];

        if (!materials.some(isLitMaterial)) return;

        object.castShadow = cast;
        object.receiveShadow = receive;
    });
};

const shadowTrackedScenes = new WeakSet<THREE.Scene>();

/**
 * Re-renders shadow maps only on frames where something that affects them
 * changed: a caster or shadow light was added, removed, hidden (including
 * via layers or material visibility), moved or given new geometry. The
 * room is static most of the time, so this skips
 * the shadow pass on nearly every frame. Requires the renderer's
 * `shadowMap.autoUpdate` to be false.
 *
 * @param {THREE.Scene} scene - The scene whose shadows should be tracked.
 */
export const updateShadowsOnChange = (scene: THREE.Scene) => {
    if (shadowTrackedScenes.has(scene)) return;
    shadowTrackedScenes.add(scene);

    // Ids, layers, geometry ids, material visibility and world matrices of
    // every caster from the last check, compared in place so a static frame allocates nothing.
    const snapshot: number[] = [];
    let length = 0;
    let hasChanged = false;
    const record = (value: number) => {
        if (snapshot[Number(length)] !== value) {
            snapshot[Number(length)] = value;
            hasChanged = true;
        }
        length++;
    };

    // Runs after three.js updates world matrices, before the shadow pass.
    type OnBeforeRender = THREE.Scene['onBeforeRender'];
    const previousOnBeforeRender = scene.onBeforeRender.bind(
        scene
    ) as OnBeforeRender;
    scene.onBeforeRender = (...args: Parameters<OnBeforeRender>) => {
        previousOnBeforeRender(...args);

        length = 0;
        hasChanged = false;
        scene.traverseVisible((object) => {
            if (!object.castShadow) return;

            const isCaster = object instanceof THREE.Mesh;
            if (!isCaster && !(object as THREE.Light).isLight) return;

            record(object.id);
            // The shadow pass skips objects off the shadow camera's layer
            // (walls hide by moving to layer 1) and invisible materials.
            record(object.layers.mask);
            if (isCaster) {
                record((object.geometry as THREE.BufferGeometry).id);

                const materials = object.material as
                    THREE.Material | THREE.Material[];
                if (Array.isArray(materials)) {
                    materials.forEach((material) =>
                        record(Number(material.visible))
                    );
                } else {
                    record(Number(materials.visible));
                }
            }
            object.matrixWorld.elements.forEach(record);
        });

        if (snapshot.length !== length) {
            snapshot.length = length;
            hasChanged = true;
        }

        if (hasChanged) {
            const [renderer] = args;
            renderer.shadowMap.needsUpdate = true;
        }
    };
};
